import * as queryString from 'query-string';

import { globalState } from '@/components/Global/GlobalDataManagement';
import { getFetchOptionsWithAuth } from '@/dataExchange/utils/fetchOptions';
import { UserDataResponseDto } from '@/domain/common';
import {
    CreateRequestParamsDto,
    DashboardLimitsUsageParamsDto,
    LimitsUsageParamsDto,
    LimitsUsageResponseDto,
    OwnLimitsUsageParamsDto,
    OwnLimitsUsageParamsDtoApiV3,
    OwnLimitsUsageResponseV3,
    RequestLimitRow,
    RequestPreviewParamsDto,
    RequestPreviewResponseDto,
    RequestResponseDto,
    UserApproverResponseDto,
    UserLimitsUsageParamsDtoApiV4,
} from '@/domain/newRequest';
import { HTTP_METHOD } from '@/utils/enums/dataExchange';

export const getAllOwnRequests = async (): Promise<RequestResponseDto[]> => {
    const response = await fetch(
        `${globalState.config.FQ_BASE_API_URL}/absence/own`,
        getFetchOptionsWithAuth({
            method: HTTP_METHOD.GET,
        })
    );

    if (!response.ok) {
        const parsedResponse = await response.json();
        throw parsedResponse;
    }

    return response.json();
};

export const getAllUserRequests = async (userId: string): Promise<RequestResponseDto[]> => {
    const response = await fetch(
        `${globalState.config.FQ_BASE_API_URL}/v2/absence/user/${userId}`,
        getFetchOptionsWithAuth({
            method: HTTP_METHOD.GET,
        })
    );

    if (!response.ok) {
        const parsedResponse = await response.json();
        throw parsedResponse;
    }

    return response.json();
};

export const getOwnLimitsUsageRequest = async (params: OwnLimitsUsageParamsDto): Promise<LimitsUsageResponseDto> => {
    const queryStringParams = queryString.stringify(params, { skipEmptyString: true, skipNull: true });

    const response = await fetch(
        `${globalState.config.FQ_BASE_API_URL}/absence/limits/usage/own?${queryStringParams}`,
        getFetchOptionsWithAuth({
            method: HTTP_METHOD.GET,
        })
    );

    if (!response.ok) {
        const parsedResponse = await response.json();
        throw parsedResponse;
    }

    return response.json();
};

/**
 * This request returns limits contained in the given period
 */
export const getOwnLimitsUsageRequestApiV3 = async (
    params: OwnLimitsUsageParamsDtoApiV3
): Promise<OwnLimitsUsageResponseV3> => {
    const queryStringParams = queryString.stringify(
        { ...params, includeUsageByOtherTypes: true },
        { skipEmptyString: true, skipNull: true }
    );

    const response = await fetch(
        `${globalState.config.FQ_BASE_API_URL}/v3/absence/limits/usage/own?${queryStringParams}`,
        getFetchOptionsWithAuth({
            method: HTTP_METHOD.GET,
        })
    );

    if (!response.ok) {
        const parsedResponse = await response.json();
        throw parsedResponse;
    }

    return response.json();
};

/**
 * This request returns already summed limits weekly/monthly/yearly for the given date
 */
export const getOwnAllLimitsUsageRequestApiV3 = async (
    params: OwnLimitsUsageParamsDtoApiV3
): Promise<OwnLimitsUsageResponseV3> => {
    const queryStringParams = queryString.stringify(
        { ...params, includeUsageByOtherTypes: true },
        { skipEmptyString: true, skipNull: true }
    );

    const response = await fetch(
        `${globalState.config.FQ_BASE_API_URL}/v3/absence/limits/usage/own?${queryStringParams}`,
        getFetchOptionsWithAuth({
            method: HTTP_METHOD.GET,
        })
    );

    if (!response.ok) {
        const parsedResponse = await response.json();
        throw parsedResponse;
    }

    return response.json();
};

/**
 * The twin of {@link getOwnAllLimitsUsageRequestApiV3} for another employee. The v3 route of this path
 * returns the flat shape the personalization panel reads, so the corrected rows live on v4.
 */
export const getUserAllLimitsUsageRequestApiV4 = async ({
    userId,
    ...params
}: UserLimitsUsageParamsDtoApiV4): Promise<OwnLimitsUsageResponseV3> => {
    const queryStringParams = queryString.stringify(
        { ...params, includeUsageByOtherTypes: true },
        { skipEmptyString: true, skipNull: true }
    );

    const response = await fetch(
        `${globalState.config.FQ_BASE_API_URL}/v4/absence/limits/usage/user/${userId}?${queryStringParams}`,
        getFetchOptionsWithAuth({
            method: HTTP_METHOD.GET,
        })
    );

    if (!response.ok) {
        const parsedResponse = await response.json();
        throw parsedResponse;
    }

    return response.json();
};

export const getUserLimitsUsage = async ({
    userId,
    rangeType,
    endDate,
    startDate,
}: LimitsUsageParamsDto): Promise<LimitsUsageResponseDto> => {
    const queryStringParams = queryString.stringify(
        { rangeType, endDate, startDate },
        { skipEmptyString: true, skipNull: true }
    );

    const response = await fetch(
        `${globalState.config.FQ_BASE_API_URL}/absence/limits/usage/${userId}?${queryStringParams}`,
        getFetchOptionsWithAuth({
            method: HTTP_METHOD.GET,
        })
    );

    if (!response.ok) {
        const parsedResponse = await response.json();
        throw parsedResponse;
    }

    return response.json();
};

export const getUserApprovers = async (userId: string): Promise<UserApproverResponseDto[]> => {
    const response = await fetch(
        `${globalState.config.FQ_BASE_API_URL}/user-absence-manager/user/${userId}`,
        getFetchOptionsWithAuth({
            method: HTTP_METHOD.GET,
        })
    );

    if (!response.ok) {
        const parsedResponse = await response.json();
        throw parsedResponse;
    }

    return response.json();
};

export const getAllApproversInOrganization = async (): Promise<UserDataResponseDto[]> => {
    const response = await fetch(
        `${globalState.config.FQ_BASE_API_URL}/user-absence-manager/managers`,
        getFetchOptionsWithAuth({
            method: HTTP_METHOD.GET,
        })
    );

    if (!response.ok) {
        const parsedResponse = await response.json();
        throw parsedResponse;
    }

    return response.json();
};

export const getRequestPreview = async ({
    userId,
    requestBody,
}: RequestPreviewParamsDto): Promise<RequestPreviewResponseDto> => {
    const response = await fetch(
        `${globalState.config.FQ_BASE_API_URL}/absence/limits/${userId}/preview/create`,
        getFetchOptionsWithAuth({
            method: HTTP_METHOD.POST,
            body: requestBody,
        })
    );

    if (!response.ok) {
        const parsedResponse = await response.json();
        throw parsedResponse;
    }

    return response.json();
};

export const createOwnRequest = async (userRequestBody: CreateRequestParamsDto): Promise<RequestResponseDto> => {
    const response = await fetch(
        `${globalState.config.FQ_BASE_API_URL}/absence`,
        getFetchOptionsWithAuth({
            method: HTTP_METHOD.POST,
            body: userRequestBody,
        })
    );

    if (!response.ok) {
        const parsedResponse = await response.json();
        throw parsedResponse;
    }

    return response.json();
};

export const createUserRequest = async ({
    userId,
    userRequestBody,
}: {
    userId?: string;
    userRequestBody: CreateRequestParamsDto;
}): Promise<RequestResponseDto> => {
    const response = await fetch(
        `${globalState.config.FQ_BASE_API_URL}/absence/${userId}`,
        getFetchOptionsWithAuth({
            method: HTTP_METHOD.POST,
            body: userRequestBody,
        })
    );

    if (!response.ok) {
        const parsedResponse = await response.json();
        throw parsedResponse;
    }

    return response.json();
};

export const getRequestPreviewBeforeAcceptance = async (absenceId: string): Promise<RequestPreviewResponseDto> => {
    const response = await fetch(
        `${globalState.config.FQ_BASE_API_URL}/absence/limits/${absenceId}/preview/acceptance`,
        getFetchOptionsWithAuth({
            contentType: null,
            method: HTTP_METHOD.GET,
        })
    );

    if (!response.ok) {
        const parsedResponse = await response.json();
        throw parsedResponse;
    }

    return response.json();
};

export const getOwnDashboardLimitsUsage = async (params: DashboardLimitsUsageParamsDto): Promise<RequestLimitRow[]> => {
    const queryStringParams = queryString.stringify(params, { skipEmptyString: true, skipNull: true });

    const response = await fetch(
        `${globalState.config.FQ_BASE_API_URL}/v4/absence/limits/usage/own/dashboard?${queryStringParams}`,
        getFetchOptionsWithAuth({
            method: HTTP_METHOD.GET,
        })
    );

    if (!response.ok) {
        const parsedResponse = await response.json();
        throw parsedResponse;
    }

    return response.json();
};
